import { Form, Formik } from "formik";
import { useState } from "react";
import PriceBreakdown from "../../components/purchase/PriceBreakdown";
import {
  ICFormikDropDown,
  ICFormikSwitch,
  ICFormikTextInput,
} from "../../core-component/formik";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import { CURRENCY_SIGN } from "../../utils/AppConstants";
import { hasAnyModification, prepareMessageFromParams } from "../../utils/AppFunctions";
import { checkPrice, isPriceBlocked } from "../../utils/PriceCalculator";
import {
  CREDIT_BALANCE_MAX,
  getCreditTypeOptions,
  getVisibilityTypeOptions,
} from "../../utils/PurchaseConstants";
import { formatCredits } from "../../utils/PurchaseFormat";
import {
  creditPlanValidationSchema,
  getCreditPlanInitialValues,
  handleSubmitCreditPlan,
  ICreditPlanForm,
} from "./CreditPlanFormController";
import { ICreditPlan } from "./CreditPlanViewController";

interface ICreditPlanFormProps {
  handleClose: (status: boolean) => void;
  creditPlanToEdit?: ICreditPlan;
}

const CreditPlanForm: React.FC<ICreditPlanFormProps> = ({
  handleClose,
  creditPlanToEdit,
}) => {
  const { t } = UseTranslationHook();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [initialValues] = useState(() => getCreditPlanInitialValues(creditPlanToEdit));

  const checkFormModification = (values: ICreditPlanForm) => {
    if (hasAnyModification(values, initialValues)) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={creditPlanValidationSchema(t)}
        onSubmit={(values) =>
          handleSubmitCreditPlan(values, setSubmitLoading, handleClose, creditPlanToEdit?.id)
        }
      >
        {(formikProps) => {
          const { handleSubmit, values } = formikProps;
          const rates = { cgst: values.cgst, sgst: values.sgst, igst: values.igst };
          const priceBlocked = isPriceBlocked(checkPrice(values.base_price, rates), false);

          return (
            <FormRightModalWrapper
              containerClassName="!w-1/3"
              onClose={() => checkFormModification(values)}
              title={creditPlanToEdit ? t("edit_credit_plan") : t("add_credit_plan")}
            >
              <Form onSubmit={handleSubmit} className="flex flex-col h-full">
                <div className="flex-1 overflow-y-scroll py-2 space-y-4">
                  <ICFormikTextInput
                    {...formikProps}
                    fieldName="name"
                    placeholder={t("name")}
                    required
                  />
                  <ICFormikDropDown
                    {...formikProps}
                    name="credit_type"
                    placeholder={t("credit_type")}
                    options={getCreditTypeOptions(t)}
                    required
                  />
                  <div>
                    <ICFormikTextInput
                      {...formikProps}
                      fieldName="credits"
                      placeholder={t("credits")}
                      type="number"
                      required
                    />
                    <div className="text-xs text-muted mt-1">
                      {`1 - ${formatCredits(t, CREDIT_BALANCE_MAX)}`}
                    </div>
                  </div>
                  <ICFormikDropDown
                    {...formikProps}
                    name="visibility_type"
                    placeholder={t("type")}
                    options={getVisibilityTypeOptions(t, false)}
                    required
                  />
                  <ICFormikTextInput
                    {...formikProps}
                    fieldName="base_price"
                    placeholder={prepareMessageFromParams(t("base_price_rupees"), [["currency", CURRENCY_SIGN]])}
                    type="number"
                    step="0.01"
                    required
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <ICFormikTextInput
                      {...formikProps}
                      fieldName="cgst"
                      placeholder={t("cgst_percentage")}
                      type="number"
                      step="0.01"
                      required
                    />
                    <ICFormikTextInput
                      {...formikProps}
                      fieldName="sgst"
                      placeholder={t("sgst_percentage")}
                      type="number"
                      step="0.01"
                      required
                    />
                    <ICFormikTextInput
                      {...formikProps}
                      fieldName="igst"
                      placeholder={t("igst_percentage")}
                      type="number"
                      step="0.01"
                      required
                    />
                  </div>
                  <PriceBreakdown basePrice={values.base_price} rates={rates} />
                  <ICFormikSwitch
                    {...formikProps}
                    name="is_active"
                    containerClassName="gap-4 !w-fit"
                    placeholder={t("active")}
                    checkedValue={BIT_VALUE.One}
                    unCheckedValue={BIT_VALUE.Zero}
                  />
                </div>
                <ICButton
                  loading={submitLoading}
                  disabled={priceBlocked || submitLoading}
                  className={cn("w-full mt-2", { "opacity-50 cursor-not-allowed": priceBlocked })}
                >
                  {t("submit")}
                </ICButton>
              </Form>
            </FormRightModalWrapper>
          );
        }}
      </Formik>
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(value) => {
            setShowDiscardWarning(false);
            value && handleClose(false);
          }}
        />
      )}
    </>
  );
};

export default CreditPlanForm;
